import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Brain, Cloud, Shield, Zap, Globe, TrendingUp, Users, ArrowRight } from 'lucide-react';
import SEOPageLayout from '../components/SEOPageLayout';

export default function WhyJoinKrieger() {
  return (
    <SEOPageLayout
      badge="Why Krieger"
      title="Who This Platform Is Built For"
      subtitle="Krieger Real Estate is designed for residential and commercial agents who want to keep more of what they earn. Sell homes, rent apartments, close commercial deals — all at up to 90% commission across New York, Pennsylvania, and Florida."
      metaTitle="Why Join Krieger Real Estate | Best Brokerage for Agents Looking to Switch"
      metaDescription="Looking for a new broker? Discover why agents switch to Krieger. Up to 90% commission, zero fees, cloud-based technology, AI tools, multi-state coverage in NY, PA, and FL."
      metaKeywords="why join krieger real estate, switch real estate broker, best brokerage for agents, looking for new broker, independent real estate agent platform, high income real estate brokerage, best brokerage to join near me, transfer real estate license"
      canonicalPath="/why-join-krieger"
    >
      <div className="space-y-12">
        <div>
          <h2 className="text-xl font-['Cabinet_Grotesk'] font-bold mb-6 text-slate-900">Why Agents Are Switching to Krieger</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { icon: TrendingUp, title: '90% Commission — Zero Fees', desc: 'Keep the lion\'s share of every deal. No desk fees, no monthly charges, no caps. The simplest split in the industry.' },
              { icon: Cloud, title: '100% Cloud-Based Operations', desc: 'Work from anywhere. All tools, documents, and systems accessible from any device. No office requirement.' },
              { icon: Brain, title: 'AI-Powered Technology', desc: 'AI chatbot, automated document workflows, electronic ICA signing, and a full transaction management platform.' },
              { icon: Globe, title: 'Statewide Coverage', desc: 'Licensed statewide in New York, Pennsylvania, and Florida. Serve commercial clients across entire states, not just metro areas.' },
              { icon: Shield, title: 'Full Compliance Support', desc: 'We handle E&O insurance, legal compliance, and regulatory requirements so you can focus on closing deals.' },
              { icon: Zap, title: 'Agency Marketing Services', desc: 'Optional marketing campaigns — social media, Google Ads, print materials, personal websites — all managed for you.' },
            ].map(item => (
              <div key={item.title} className="bg-white border border-slate-200 p-6 rounded-xl">
                <div className="w-10 h-10 bg-[#EFF3FF] rounded-xl flex items-center justify-center mb-4"><item.icon size={18} className="text-[#002FA7]" /></div>
                <h3 className="text-base font-['Cabinet_Grotesk'] font-medium mb-2 text-slate-800">{item.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-7">
          <h2 className="text-xl font-['Cabinet_Grotesk'] font-bold mb-4 text-slate-900">The Ideal Krieger Agent</h2>
          <div className="space-y-3">
            {[
              'Commercial sales agents doing $3M+ in annual volume',
              'Independent operators who don\'t need hand-holding',
              'Agents selling multifamily, office, retail, self storage, or warehouse',
              'Leasing agents representing tenants and landlords',
              'Commercial agents looking to operate statewide in NY, PA, or FL',
              'Tech-savvy professionals who value cloud-based tools',
            ].map(item => (
              <div key={item} className="flex items-start gap-3 text-sm text-slate-600">
                <CheckCircle2 size={16} className="text-[#002FA7] shrink-0 mt-0.5" /> {item}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#002FA7] text-white rounded-xl p-8 md:p-10">
          <h2 className="text-2xl font-['Cabinet_Grotesk'] font-bold mb-3">The Math Is Simple</h2>
          <p className="text-blue-100 text-sm mb-6 max-w-lg">On a $500,000 sale at 3% commission, the gross is $15,000. At Krieger's 90/10 split, you keep $13,500. At a traditional 70/30 split, you'd keep only $10,500. That's $3,000 more per deal — on every deal.</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/agent-income-calculator" className="inline-flex items-center gap-2 bg-white text-[#002FA7] px-6 py-2.5 rounded-full font-medium text-sm hover:bg-blue-50 transition-colors">
              Calculate Your Earnings <ArrowRight size={14} />
            </Link>
            <Link to="/commission-plans" className="inline-flex items-center gap-2 border border-white/30 text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-white/10 transition-colors">
              View Commission Plans
            </Link>
          </div>
        </div>
      </div>
    </SEOPageLayout>
  );
}
